import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

// Accepts both a React element (ReactNode) and a component reference (ElementType)
// so callers can use either <EmptyState icon={<Search />} /> or <EmptyState icon={Search} />
type IconProp = React.ElementType | React.ReactNode;

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: "default" | "outline" | "ghost";
}

/**
 * The one empty-state in the app. Two looks:
 *  - "hero" (default): a large, centred, teal-ringed state for a full page or a
 *    section that is entirely empty.
 *  - "compact": a small dashed-border card for an empty region inside a busy
 *    screen (admin tables, a card's body) where the hero would be too loud.
 * (Consolidated from the former primitives/EmptyState — that was the compact one.)
 */
type EmptyStateVariant = "hero" | "compact";

interface EmptyStateProps {
  icon?: IconProp;
  title: string;
  description?: string;
  variant?: EmptyStateVariant;
  /** Single ReactNode action (new API) */
  action?: React.ReactNode;
  /** Array of action buttons (backward-compat API) */
  actions?: EmptyStateAction[];
  className?: string;
}

function renderIcon(icon: IconProp | undefined, iconClassName: string): React.ReactNode {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  if (typeof icon === "function" || typeof icon === "string" || typeof icon === "object") {
    const Comp = icon as React.ElementType;
    return <Comp className={iconClassName} />;
  }
  return icon as React.ReactNode;
}

export function EmptyState({
  icon,
  title,
  description,
  variant = "hero",
  action,
  actions = [],
  className,
}: EmptyStateProps) {
  const hasActions = action || actions.length > 0;

  if (variant === "compact") {
    const iconEl = renderIcon(icon, "h-6 w-6 text-muted-foreground");
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-10 text-center",
          className
        )}
        role="status"
        aria-label={title}
      >
        {iconEl && (
          <div className="rounded-full bg-background p-3" aria-hidden="true">
            {iconEl}
          </div>
        )}
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {description && (
            <p className="max-w-xs text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        {hasActions && (
          <div className="flex flex-wrap gap-2 justify-center">
            {action}
            {actions.map((a, i) => (
              <Button key={i} size="sm" variant={a.variant ?? (i === 0 ? "default" : "outline")} onClick={a.onClick}>
                {a.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // hero
  const iconEl = renderIcon(icon, "h-8 w-8 text-muted-foreground");
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-12 text-center",
        className
      )}
      role="status"
      aria-label={title}
    >
      {iconEl && (
        /* Concentric teal rings behind the glyph: an empty screen is the one
           place with room for a little warmth, and this reads as considered
           rather than broken. Decorative only — the ring is aria-hidden and
           the state is announced by role="status" on the wrapper. */
        <div className="relative flex items-center justify-center" aria-hidden="true">
          <span className="absolute h-24 w-24 rounded-full bg-kanfy-tint-teal/50 motion-safe:animate-pulse" />
          <span className="absolute h-20 w-20 rounded-full bg-kanfy-tint-teal" />
          <div className="relative h-16 w-16 rounded-full bg-card shadow-e1 flex items-center justify-center">
            {iconEl}
          </div>
        </div>
      )}
      <p className="mt-5 text-xl font-bold tracking-tight text-foreground">{title}</p>
      {description && (
        <p className="mt-1 text-sm text-muted-foreground max-w-sm">
          {description}
        </p>
      )}
      {hasActions && (
        <div className="mt-4 flex flex-wrap gap-3 justify-center">
          {action}
          {actions.map((a, i) => (
            <Button
              key={i}
              variant={a.variant ?? (i === 0 ? "default" : "outline")}
              onClick={a.onClick}
            >
              {a.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
